if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }
          mediaToggle.removeAttribute("aria-haspopup");
          mediaToggle.setAttribute("aria-current", thumbnail.classList.contains("is-active") ? "true" : "false");
          mediaToggle.addEventListener(
            "click",
            this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
          );
          // AJ 2026-10-18 03:37 ("when i click on cartridge and switch pictures its blank grey then the image loads"):
          // reaching for a thumbnail (hover, focus or finger down) starts its large photo loading before the click.
          const reach = () => this.prime(this.mediaElement(this.elements.mains, mediaToggle.dataset.mediaId), "high");
          ["pointerenter", "focus", "pointerdown"].forEach((type) => mediaToggle.addEventListener(type, reach, { passive: true }));
        });
        // ...and once the page is idle, the photos either side of the first one load at low priority.
        const warmNeighbors = () => {
          const mains = [...this.elements.mains];
          const at = Math.max(0, mains.findIndex((element) => element.classList.contains("is-active")));
          [1, 2, -1].forEach((step) => this.prime(mains[(at + step + mains.length) % mains.length], "low"));
        };
        if (document.readyState === "complete") (window.requestIdleCallback || setTimeout)(warmNeighbors);
        else window.addEventListener("load", () => (window.requestIdleCallback || setTimeout)(warmNeighbors), { once: true });
      }

      connectedCallback() {
        this.setupStickyGallery();
        this.productPage = this.closest(".cq-product-page");
        if (this.productPage && !this.variantMediaHandler) {
          this.variantMediaHandler = (event) => {
            const variant = event.detail?.variant;
            if (this.showVariantPhotos(variant)) return;
            if (variant?.mediaId) this.setActiveMedia(variant.mediaId, false);
          };
          this.productPage.addEventListener("cq:variant-change", this.variantMediaHandler);
          // P1 (AJ 2026-10-11, "the images load quite slowly"): reaching for an option (finger down, focus or a key)
          // starts loading the photos its other values show, so the change that follows is usually instant.
          this.intentEvents = new AbortController();
          const warm = (event) => {
            const group = event.target.closest?.("cq-purchase-options [data-cq-option]");
            if (group) this.warmOptionPhotos(group);
          };
          ["pointerdown", "focusin", "keydown"].forEach((type) => {
            this.productPage.addEventListener(type, warm, { capture: true, passive: true, signal: this.intentEvents.signal });
          });
        }
        this.photoKey = this.shownPhotoIds().join(",");

        if (!window.Flickity) {
          return;
        }

        this.elements.slideshow.style.display = "block";
        this.createFlickity();
      }

      createFlickity() {
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          // r7: photos the selected option does not show are not slides.
          cellSelector: ".product-media:not(.cq-photo-off)",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: !this.productPage,
          watchCSS: true,
          on: {
            change: (index) => {
              const { element } = this.flickity.cells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId);
              this.warmNeighbours();
            },
          },
        });
        // P1 (AJ 2026-10-11: "i dont like how easy it is to accidentally scroll side ways on product pages because of the
        // product image"): Flickity moved the photo after 3 px sideways, so a scroll that started a little crooked
        // slid it. The photo now follows only a finger that clearly travels sideways: more than 30 px across and at least
        // twice as far across as up or down. The direction is settled after the first 10 px, so a scroll that later
        // drifts sideways never starts a swipe.
        // AJ 2026-10-21 ("when trying to scroll through pictures with swipe on mobile, it's too easy to accidentally
        // scroll down"): Flickity listens for touchmove on window, which browsers treat as passive, so its
        // preventDefault never held the page still, and touch-action: pan-y let any vertical drift scroll the page
        // while the photo moved. Now the direction is settled once per gesture after 10 px: sideways when the finger
        // has gone at least 0.7 times as far across as up or down. A sideways gesture holds the page still (our own
        // non-passive touchmove on the viewport) and moves the photo; any other gesture scrolls the page and leaves
        // the photo alone. Two fingers are a pinch: never blocked, never a swipe. Taps still open the photo viewer.
        const flickity = this.flickity;
        let axis = "";
        const settle = (dx, dy) => {
          const x = Math.abs(dx);
          const y = Math.abs(dy);
          if (!axis && Math.max(x, y) >= 10) axis = x >= 0.7 * y ? "x" : "y";
          return x >= 0.7 * y;
        };
        flickity.on("pointerDown", () => {
          if (axis !== "pinch") axis = "";
          this.warmNeighbours();
        });
        flickity.hasDragStarted = (move) => {
          settle(move.x, move.y);
          return axis === "x";
        };
        // Pinch-zoom stays available on the photo (Flickity's default pan-y turns it off).
        flickity._touchActionValue = "pan-y pinch-zoom";
        flickity.viewport.style.touchAction = flickity._touchActionValue;
        let start = null;
        const viewport = flickity.viewport;
        viewport.addEventListener(
          "touchstart",
          (event) => {
            if (event.touches.length > 1) {
              axis = "pinch";
              start = null;
              return;
            }
            axis = "";
            start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
          },
          { passive: true },
        );
        viewport.addEventListener(
          "touchmove",
          (event) => {
            if (!start || axis === "pinch" || event.touches.length > 1 || !event.cancelable) return;
            const touch = event.touches[0];
            const sideways = settle(touch.clientX - start.x, touch.clientY - start.y);
            // Before the direction is settled a sideways-leaning move already holds the page, so the page never
            // starts scrolling under a swipe (iOS decides on the first touchmove).
            if (axis === "x" || (!axis && sideways)) event.preventDefault();
          },
          { passive: false },
        );
        // Phone slides hidden from screen readers also leave the keyboard order (shared helper in scripts.js).
        window.cqCarouselInert?.(this.elements.slideshow, ".product-media");
        // Neighbours load after the page has (never ahead of the first photo), or as soon as a finger is on the photo.
        if (document.readyState === "complete") this.warmNeighbours();
        else window.addEventListener("load", () => this.warmNeighbours(), { once: true });
      }

      // The slides next to the shown one load ahead (low priority), so a swipe never lands on an empty tile.
      warmNeighbours() {
        const flickity = this.flickity;
        if (!flickity?.isActive || flickity.cells.length < 2) return;
        const count = flickity.cells.length;
        [1, -1].forEach((step) => {
          this.prime(flickity.cells[(flickity.selectedIndex + step + count) % count]?.element, "low");
        });
      }

      // Starts loading a slide's photo now: the file its srcset and sizes pick, at the given priority.
      prime(element, priority) {
        const image = element?.querySelector("img");
        if (!image || (image.complete && image.naturalWidth)) return;
        if (priority === "high" || image.fetchPriority !== "high") image.fetchPriority = priority;
        image.loading = "eager";
      }

      mediaElement(list, id) {
        return [...list].find((element) => element.dataset.mediaId === String(id));
      }

      // P1: the photos the other values of an option group lead with, given the shopper's other choices (the same
      // variant the picker would choose for an exact match, else the first one in stock). Every screen loads their
      // small thumbnails, which also stand in while a large photo is still arriving; phones, where the slow loads
      // were seen, also load the carousel's own file at low priority. At most six photos per group.
      warmOptionPhotos(group) {
        const picker = group.closest("cq-purchase-options");
        const variants = picker?.variants;
        const current = picker?.variant;
        if (!Array.isArray(variants) || !current?.options) return;
        const index = Number(group.dataset.cqOption);
        const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
        const ids = new Set();
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.disabled) return;
          const matches = variants.filter((variant) => same(variant.options?.[index], input.value));
          const keeps = (variant) => variant.options.every((value, i) => i === index || same(value, current.options[i]));
          const variant = matches.find(keeps) || matches.find((item) => item.available) || matches[0];
          if (variant?.mediaId) ids.add(String(variant.mediaId));
        });
        const phone = window.matchMedia("(max-width: 749px)").matches;
        [...ids].slice(0, 6).forEach((id) => {
          const thumbnail = this.mediaElement(this.elements.thumbnails, id)?.querySelector("img");
          if (thumbnail?.loading === "lazy") thumbnail.loading = "eager";
          if (phone) this.prime(this.mediaElement(this.elements.mains, id), "low");
        });
      }

      // P1: an option's photo shown before its file has arrived has its thumbnail (small, loaded first, see
      // warmOptionPhotos) behind it, so the tile is never empty; the photo covers it once loaded.
      standIn(main) {
        const image = main?.querySelector("img");
        const box = image?.parentElement;
        const thumbnail = this.mediaElement(this.elements.thumbnails, main?.dataset.mediaId)?.querySelector("img");
        if (!box || !thumbnail || (image.complete && image.naturalWidth)) return;
        const show = () => {
          if (image.complete && image.naturalWidth) return;
          box.style.setProperty("--cq-stand-in", `url("${thumbnail.currentSrc.replace(/"/g, "%22")}")`);
          box.toggleAttribute("data-cq-stand-in", true);
        };
        if (thumbnail.complete && thumbnail.naturalWidth) show();
        else thumbnail.addEventListener("load", show, { once: true });
        ["load", "error"].forEach((type) => image.addEventListener(type, () => box.removeAttribute("data-cq-stand-in"), { once: true }));
      }

      shownPhotoIds() {
        return [...this.elements.mains]
          .filter((element) => !element.classList.contains("cq-photo-off"))
          .map((element) => element.dataset.mediaId);
      }

      // r7 (AJ 2026-10-08): an option change shows that option's photos in the main image, the thumbnail
      // row and the zoom modal, without a fetch (rules in assets/cq-option-picker.js, CQVariantPhotos).
      // Returns false when the product shows every photo, so the caller just moves to the option's photo.
      showVariantPhotos(variant) {
        const photos = window.CQVariantPhotos;
        const mode = this.dataset.cqPhotos;
        if (!photos || !mode || (mode === "all" && !this.dataset.cqPhotoOrder)) return false;
        const mains = [...this.elements.mains];
        const ids = photos.plan(mains.map((element) => photos.read(element)), mode, variant?.mediaId, photos.pick?.(this, variant), this.dataset.cqPhotoOrder);
        if (!ids.length) return false;
        const key = ids.join(",");
        const token = (this.photoToken = (this.photoToken || 0) + 1);
        if (key === this.photoKey) {
          this.setActiveMedia(ids[0], false);
          return true;
        }
        // Start the new thumbnails loading, and wait briefly for the new main photo (high priority), while the current
        // set stays. A newer option change takes over (token), so only the last choice is applied.
        const wanted = new Set(ids);
        this.elements.thumbnails.forEach((thumbnail) => {
          if (!wanted.has(thumbnail.dataset.mediaId)) return;
          thumbnail.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = "eager"; });
        });
        photos.ready(this.mediaElement(mains, ids[0])).then(() => {
          if (token === this.photoToken && this.isConnected) this.applyVariantPhotos(ids);
        });
        return true;
      }

      applyVariantPhotos(ids) {
        const photos = window.CQVariantPhotos;
        // P1 (AJ 2026-10-11 on his iPhone: after an option change the photo "just straight up doesnt load.. cant change
        // image, cant click on empty space and open it, just blank ... unless you refresh"): this used to destroy the
        // carousel and build a new one. Flickity 2.3's destroy() leaves its debounced resize running, and an iPhone
        // resizes the window whenever its toolbar shows or hides; within 150 ms of the tap the old carousel woke up
        // (watchCSS), took every slide into a second, hidden viewport and left the visible one empty, for good.
        // Now the one carousel keeps running: the slides are re-ordered inside it and it re-reads its cells.
        const flickity = this.flickity?.isActive ? this.flickity : null;
        photos.arrange(flickity ? flickity.slider : this.elements.slideshow, [...this.elements.mains], ids);
        if (flickity) {
          // reloadCells() would first put the old wrap-around copies back at their old places, over the new layout.
          flickity.beforeShiftCells = [];
          flickity.afterShiftCells = [];
          flickity.selectedIndex = 0;
          flickity.reloadCells();
          flickity.select(0, false, true);
        }
        const track = this.querySelector(".product-media-thumbnails");
        photos.arrange(track, [...this.elements.thumbnails], ids);
        const modal = document.getElementById(this.id.replace("MediaGallery-", "ProductModal-"));
        const modalContent = modal?.querySelector(".product-media-modal-content");
        if (modalContent) photos.arrange(modalContent, [...modalContent.querySelectorAll(":scope > [data-cq-pos]")], ids);

        // Screen reader labels count the photos this option shows.
        const label = (element, template, index) => {
          const text = element?.querySelector(".product-media-toggle .visually-hidden");
          if (text && template) text.textContent = template.replace("__N__", index + 1);
        };
        ids.forEach((id, index) => {
          label(this.mediaElement(this.elements.thumbnails, id), this.dataset.cqLoadLabel, index);
          // r10 item 12: an image's button is "Zoom image N"; video and 3D keep the modal wording.
          const main = this.mediaElement(this.elements.mains, id);
          label(main, main?.querySelector(".product-modal-opener-image") ? this.dataset.cqZoomLabel || this.dataset.cqOpenLabel : this.dataset.cqOpenLabel, index);
        });

        if (track) track.scrollLeft = 0;
        this.photoKey = ids.join(",");
        this.setActiveMedia(ids[0], false);
        this.standIn(this.mediaElement(this.elements.mains, ids[0]));
        this.warmNeighbours();
        this.querySelector("cq-thumb-rail")?.schedule?.();
      }

      setupStickyGallery() {
        if (!this.closest(".cq-product-page")) return;
        this.galleryWrap = this.closest(".product-media-gallery-wrap");
        if (!this.galleryWrap || this.stickyGalleryHandler) return;
        this.galleryTop = null;
        this.gallerySticky = null;
        // A7 4e: the gallery pins only from 1024 px; narrower screens do no work per scroll frame.
        this.stickyGalleryQuery = window.matchMedia("(min-width: 1024px)");

        this.stickyGalleryHandler = () => {
          if (this.stickyGalleryFrame || (!this.stickyGalleryQuery.matches && !this.gallerySticky)) return;
          this.stickyGalleryFrame = requestAnimationFrame(() => {
            this.stickyGalleryFrame = null;
            this.updateStickyGallery();
          });
        };
        window.addEventListener("scroll", this.stickyGalleryHandler, { passive: true });
        window.addEventListener("resize", this.stickyGalleryHandler);
        this.stickyGalleryObserver = new ResizeObserver(this.stickyGalleryHandler);
        this.stickyGalleryObserver.observe(this.galleryWrap);
        this.galleryHeader = document.querySelector(".cq-header-bar, .section-header");
        if (this.galleryHeader) {
          this.stickyGalleryObserver.observe(this.galleryHeader);
          this.galleryHeaderObserver = new MutationObserver(this.stickyGalleryHandler);
          this.galleryHeaderObserver.observe(this.galleryHeader, { attributes: true, attributeFilter: ["class"] });
          this.galleryHeader.addEventListener("transitionend", this.stickyGalleryHandler);
        }
        // In a frame: a call here forced a layout while the page was still parsing.
        this.stickyGalleryHandler();
      }

      updateStickyGallery() {
        // Below 1024 px: undo a pin left from a wider window, before any read.
        if (!this.stickyGalleryQuery.matches) {
          if (this.gallerySticky) this.galleryWrap.classList.remove("cq-gallery-sticky");
          this.gallerySticky = false;
          return;
        }
        const bar = document.querySelector(".cq-header-bar");
        const legacy = document.querySelector(".section-header");
        const legacyEnabled = legacy?.matches(".section-header-sticky, .section-header-sticky-always") &&
          !legacy.querySelector('sticky-header[disabled="true"]');
        const header = bar || (legacyEnabled ? legacy : null);
        const rect = header?.getBoundingClientRect();
        const bottom = rect?.width && rect.height && rect.top < window.innerHeight
          ? Math.max(0, Math.min(rect.bottom, window.innerHeight)) : 0;
        const top = bottom + 20;
        // Read every dimension before changing styles; writing top before reading the
        // gallery height forced a second layout on each scroll frame.
        const sticky = this.galleryWrap.getBoundingClientRect().height <= window.innerHeight - top - 20;

        if (top !== this.galleryTop) {
          this.galleryWrap.style.setProperty("--cq-gallery-top", `${top}px`);
          this.galleryTop = top;
        }
        // Never pin a gallery whose lower media, controls or disclaimer would be hidden.
        if (sticky !== this.gallerySticky) {
          this.galleryWrap.classList.toggle("cq-gallery-sticky", sticky);
          this.gallerySticky = sticky;
        }
      }

      disconnectedCallback() {
        this.productPage?.removeEventListener("cq:variant-change", this.variantMediaHandler);
        this.variantMediaHandler = null;
        window.removeEventListener("scroll", this.stickyGalleryHandler);
        window.removeEventListener("resize", this.stickyGalleryHandler);
        this.stickyGalleryObserver?.disconnect();
        this.galleryHeaderObserver?.disconnect();
        this.galleryHeader?.removeEventListener("transitionend", this.stickyGalleryHandler);
        cancelAnimationFrame(this.stickyGalleryFrame);
        this.stickyGalleryHandler = null;
        this.stickyGalleryFrame = null;
        this.intentEvents?.abort();
        this.intentEvents = null;
        const flickity = this.flickity;
        this.flickity = null;
        if (flickity) {
          flickity.destroy();
          // destroy() leaves the debounced resize and a running settle animation behind; stop both (see applyVariantPhotos).
          clearTimeout(flickity.onresizeTimeout);
          flickity.isAnimating = false;
          flickity.watchCSS = () => {};
        }
      }

      setActiveMedia(mediaId, prepend) {
        if (!mediaId) return;
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = this.mediaElement(this.elements.mains, mediaId);
        if (!nextMainMedia) return;
        // P1: the photo about to show starts loading now, first in line (it may still be lazy and off screen).
        this.prime(nextMainMedia, "high");
        // Its thumbnail stands in until it arrives, so switching photos never shows an empty tile (AJ 2026-10-18 03:37).
        this.standIn(nextMainMedia);
        const index = this.flickity?.isActive ? this.flickity.cells.findIndex((cell) => cell.element === nextMainMedia) : -1;

        if (this.flickity?.isActive && index >= 0 && this.flickity.selectedIndex !== index) {
          this.flickity.select(index, false, window.matchMedia("(prefers-reduced-motion: reduce)").matches);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
          thumb.querySelector("button")?.setAttribute("aria-current", "false");
        });
        const thumbnail = this.mediaElement(this.elements.thumbnails, mediaId);

        activeMainMedia?.classList.remove("is-active");

        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        thumbnail?.querySelector("button")?.setAttribute("aria-current", "true");
        this.playActiveMedia(nextMainMedia);

        if (!thumbnail) {
          // The code below is for thumbnails, return early
          // if there are not visible (e.g. in the quick view drawer).
          return;
        }

        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else {
          const scrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth";
          const container = thumbnail.parentElement;
          const containerRect = container.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          // r5: the PDP rail fades its edges; keep the active thumbnail clear of the fade.
          const edge = container.hasAttribute("data-cq-rail-track") ? 40 : 0;

          const isHorizontalScroll = container.scrollWidth > container.clientWidth;

          if (isHorizontalScroll) {
            if (thumbnailRect.left < containerRect.left + edge) {
              container.scrollBy({
                left: thumbnailRect.left - containerRect.left - edge,
                behavior: scrollBehavior,
              });
            } else if (thumbnailRect.right > containerRect.right - edge) {
              container.scrollBy({
                left: thumbnailRect.right - containerRect.right + edge,
                behavior: scrollBehavior,
              });
            }
          } else {
            if (thumbnailRect.top < containerRect.top) {
              container.scrollBy({
                top: thumbnailRect.top - containerRect.top,
                behavior: scrollBehavior,
              });
            } else if (thumbnailRect.bottom > containerRect.bottom) {
              container.scrollBy({
                top: thumbnailRect.bottom - containerRect.bottom,
                behavior: scrollBehavior,
              });
            }
          }
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector("deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}

if (!customElements.get("product-thumbnail-navigation")) {
  customElements.define(
    "product-thumbnail-navigation",
    class ProductThumbnailNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.gallery = document.querySelector(`#${this.getAttribute("for")}`);
        this.elements = {
          gallery: this.gallery,
          scroller: this.gallery.querySelector(".product-media-scroller"),
          thumbnailWrapper: this.gallery.querySelector(".product-media-thumbnails"),
          thumbnails: this.gallery.querySelectorAll(".product-media-thumbnail"),
          buttonPrev: this.querySelector(".js-prev"),
          buttonNext: this.querySelector(".js-next"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        // Detect if scrolling is horizontal or vertical
        this.isHorizontal =
          this.elements.scroller.clientWidth < this.elements.thumbnailWrapper.scrollWidth;
        const isVertical =
          this.elements.scroller.clientHeight < this.elements.thumbnailWrapper.scrollHeight;

        // Add class to hide gradients when no overflow
        if (!this.isHorizontal && !isVertical) {
          this.elements.scroller.classList.add("no-overflow");
        } else {
          this.elements.scroller.classList.remove("no-overflow");
        }

        if (this.isHorizontal || isVertical) {
          this.classList.remove("hidden");

          this.elements.buttonPrev.addEventListener("click", (event) => {
            event.preventDefault();
            this.prev();
          });

          this.elements.buttonNext.addEventListener("click", (event) => {
            event.preventDefault();
            this.next();
          });

          this.elements.thumbnailWrapper.addEventListener("scroll", () => {
            this.buttonAvailability();
          });
        }
      }

      prev() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos - 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos - 300);
        }
      }

      next() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos + 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos + 300);
        }
      }

      buttonAvailability() {
        const el = this.elements.thumbnailWrapper;
        let hasBottomed, hasTopped;

        if (this.isHorizontal) {
          hasBottomed = el.scrollLeft + el.clientWidth >= el.scrollWidth - 30;
          hasTopped = el.scrollLeft <= 30;
        } else {
          hasBottomed = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
          hasTopped = el.scrollTop <= 30;
        }

        this.elements.buttonPrev.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.classList.remove("pill-nav-item-disabled");

        if (hasBottomed) {
          this.elements.buttonNext.classList.add("pill-nav-item-disabled");
        }

        if (hasTopped) {
          this.elements.buttonPrev.classList.add("pill-nav-item-disabled");
        }
      }
    },
  );
}

if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");

        this.zoomWidth = parseInt(this.getAttribute("data-zoom-width"), 10) || 150;
        this.zoomHeight = parseInt(this.getAttribute("data-zoom-height"), 10) || 150;

        this.lastEvent = null;
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.innerHTML += `<div class="media-zoom-zoom-area"></div>`;
        this.original = this.querySelector("img");
        this.zoomArea = this.querySelector(".media-zoom-zoom-area");

        this.original.addEventListener("mouseenter", this.onMouseEnter);
        this.original.addEventListener("mousemove", this.onMouseMove);
        this.original.addEventListener("mouseleave", this.onMouseLeave);

        if (this.mode === "lightbox_zoom_hover") {
          this.original.addEventListener("click", this.onClick);
        }

        this.zoomArea.style.width = `${this.zoomWidth}px`;
        this.zoomArea.style.height = `${this.zoomHeight}px`;

        this.preview = document.createElement("div");
        this.preview.classList.add("media-zoom-preview-area", "color-background-1");
        this.preview.innerHTML = '<div class="spinner"></div>';
        document.body.appendChild(this.preview);
      }

      disconnectedCallback() {
        this.original.removeEventListener("mouseenter", this.onMouseEnter);
        this.original.removeEventListener("mousemove", this.onMouseMove);
        this.original.removeEventListener("mouseleave", this.onMouseLeave);
        document.body.removeChild(this.preview);
      }

      onMouseEnter() {
        this.zoomArea.style.display = "block";
        this.preview.style.display = "block";

        const previewSize = `${this.original.offsetWidth}px`;
        this.preview.style.width = previewSize;
        this.preview.style.height = previewSize;

        // Load larger image on first mouse enter
        if (!this.previewImg) {
          this.previewImg = new Image();
          this.previewImg.src = this.getAttribute("data-zoom");
          this.previewImg.onload = () => {
            const zoomScale = this.original.offsetWidth / this.zoomWidth;

            this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
            this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;

            this.preview.innerHTML = "";
            this.preview.appendChild(this.previewImg);
            if (this.lastEvent) {
              this.original.dispatchEvent(new MouseEvent("mousemove", this.lastEvent));
            }
          };
        } else {
          // Set the correct preview image always on mouse enter
          const zoomScale = this.original.offsetWidth / this.zoomWidth;
          this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
          this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;
        }
      }

      onMouseMove(e) {
        this.lastEvent = e;
        const { left, top, width, height } = this.original.getBoundingClientRect();

        const x = e.pageX - (window.pageXOffset + left);
        const y = e.pageY - (window.pageYOffset + top);

        const zoomX = Math.min(Math.max(x - this.zoomWidth / 2, 0), width - this.zoomWidth);
        const zoomY = Math.min(Math.max(y - this.zoomHeight / 2, 0), height - this.zoomHeight);

        this.zoomArea.style.left = `${zoomX}px`;
        this.zoomArea.style.top = `${zoomY}px`;

        this.preview.style.position = "fixed";
        this.preview.style.left = `${left + width + 50}px`;
        this.preview.style.top = `${top - 19}px`;

        if (this.previewImg && this.previewImg.complete) {
          const previewX = (zoomX / width) * this.previewImg.width;
          const previewY = (zoomY / height) * this.previewImg.height;

          this.previewImg.style.transform = `translate(${-previewX}px, ${-previewY}px)`;
        }
      }

      onMouseLeave() {
        this.zoomArea.style.display = "none";
        this.preview.style.display = "none";
      }

      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}
